// =============================================================================
// useLeaderboard
// =============================================================================
// PURPOSE: Leaderboard data with Supabase Realtime subscriptions for instant updates.
//
// ARCHITECTURE:
// - Supabase Realtime subscription on daily_leaderboard table (green "Live" indicator when connected)
// - Automatic fallback to 30s polling if Realtime fails or disconnects
// - AbortController cleanup for race conditions on filter/org changes
// - Supports: date range aggregation, sector filtering, pod filtering, company-wide view
//
// WEIGHTED SCORING:
// - Closers: 50% cash collected, 25% show rate, 25% CPC
// - Setters: 40% CPC, 30% show rate, 30% calls booked
// - Dialers: 30% CPC, 25% daily dials, 25% connect-to-book, 20% show rate
// - Formulas defined in backend (daily_leaderboard rollup function)
//
// VIEW MODES:
// - Daily: Single day snapshot (default)
// - Aggregate: Sum stats across date range, recalculate derived metrics
// - Pod vs Company-wide: Filter to single org or aggregate all accessible orgs
//
// KEY DECISIONS:
// - Realtime updates single entry optimistically (no full refetch on change)
// - Polling only runs when tab is visible (document.visibilityState check)
// - Date range defaults to "today" if not specified
// =============================================================================

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { supabase } from "../../lib/supabase";
import { LeaderboardEntry } from "../../types";
import { logger } from "../../lib/logger";
import { useRealtimeSubscription, RealtimeStatus } from "./useRealtimeSubscription";
import { CACHE_VERSION } from "../../lib/cacheVersion";
import { toOrgDateKey } from "../../lib/timezone";

// =============================================================================
// Cache configuration
// =============================================================================
const LB_CACHE_PREFIX = `altara_${CACHE_VERSION}_lb_`;
const LB_CACHE_TTL = 2 * 60 * 1000; // 2 minutes

// Stable reference for useRealtimeSubscription `events` prop. Inline literal
// would create a new array each render → useCallback dep drift → channel
// teardown/rebuild loop. See docs/handoff/[doing]_NAV_LAG_REALTIME_THRASH.md.
const LB_REALTIME_EVENTS: ("INSERT" | "UPDATE")[] = ["INSERT", "UPDATE"];

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  orgId: string;
}

/**
 * Get cached leaderboard data if valid (not expired, same org)
 */
function getLbCache<T>(key: string, orgId: string): T | null {
  try {
    const raw = localStorage.getItem(LB_CACHE_PREFIX + key);
    if (!raw) return null;
    const entry: CacheEntry<T> = JSON.parse(raw);
    if (Date.now() - entry.timestamp > LB_CACHE_TTL || entry.orgId !== orgId) {
      localStorage.removeItem(LB_CACHE_PREFIX + key);
      return null;
    }
    return entry.data;
  } catch {
    return null;
  }
}

/**
 * Store leaderboard data in cache with org scope
 */
function setLbCache<T>(key: string, data: T, orgId: string): void {
  try {
    localStorage.setItem(
      LB_CACHE_PREFIX + key,
      JSON.stringify({ data, timestamp: Date.now(), orgId })
    );
  } catch {
    /* localStorage full — ignore */
  }
}

/**
 * Generate cache key from filters and date range
 */
function getLbCacheKey(
  orgId: string,
  sector: string | undefined,
  podId: string | undefined,
  startDate: string,
  endDate: string
): string {
  return `${orgId}_${sector || "all"}_${podId || "all"}_${startDate}_${endDate}`;
}

export interface LeaderboardFilters {
  /** Filter by sector (closer, setter, dialer) */
  sector?: string;
  /** Filter by pod ID */
  podId?: string;
  /** Start date (inclusive) */
  startDate?: Date;
  /** End date (inclusive) - defaults to today */
  endDate?: Date;
  /** View mode: single day vs date range aggregate */
  viewMode?: "daily" | "aggregate";
}

export interface UseLeaderboardOptions {
  /** Organization ID to fetch for */
  orgId: string | null;
  /** All organization IDs user has access to (for company-wide view) */
  allOrgIds?: string[];
  /** Whether to fetch company-wide leaderboard */
  includeCompanyWide?: boolean;
  /** Filters */
  filters?: LeaderboardFilters;
  /** Whether to use Realtime (default: true) */
  enableRealtime?: boolean;
  /** Polling interval in ms when Realtime unavailable (default: 30000) */
  pollingInterval?: number;
}

export interface UseLeaderboardResult {
  /** Leaderboard entries for current org */
  leaderboard: LeaderboardEntry[];
  /** Company-wide leaderboard (all accessible orgs) */
  companyWideLeaderboard: LeaderboardEntry[];
  /**
   * Per-rep KPI ±1 sums over the viewed range (get_kpi_delta_sums RPC).
   * Keyed by rep_id ALONE — NOT "{org}:{rep}": the page's range aggregation
   * stamps the current org onto every aggregated entry, so an org-composite
   * key silently misses for other-org reps in company view.
   */
  kpiDeltas: Map<string, number>;
  /** user_ids with organization_members.status='active' for the queried orgs */
  activeRepIds: Set<string>;
  /** Whether currently loading */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Realtime connection status */
  realtimeStatus: RealtimeStatus;
  /** Whether receiving real-time updates */
  isRealtimeConnected: boolean;
  /** Manually refresh data */
  refresh: () => Promise<void>;
  /** Update filters */
  setFilters: (filters: LeaderboardFilters) => void;
}

// P0f (May 2, 2026): aggregateLeaderboard removed. The page's computeSectorEntries
// (LeaderboardPage.tsx) does the correct per-day → aggregate transformation. The hook
// now returns raw daily rows so the page can filter by date before aggregating.

export function useLeaderboard(options: UseLeaderboardOptions): UseLeaderboardResult {
  const {
    orgId,
    allOrgIds = [],
    includeCompanyWide = false,
    filters: initialFilters,
    enableRealtime = true,
    pollingInterval = 30000,
  } = options;

  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [companyWideLeaderboard, setCompanyWideLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // P0-LBTOTAL S3: prestige-total inputs. Fetched fresh on EVERY path (cache
  // hit included) — deliberately NOT part of the localStorage row cache
  // (tiny payloads; roster status must never go stale).
  const [kpiDeltas, setKpiDeltas] = useState<Map<string, number>>(() => new Map());
  const [activeRepIds, setActiveRepIds] = useState<Set<string>>(() => new Set());
  // Latest roster for the realtime patch path. null = roster unknown (fetch
  // failed) → skip the inactive filter rather than hide every rep. A ref, not
  // a dep, so the realtime callback identity doesn't churn per fetch.
  const activeRepIdsRef = useRef<Set<string> | null>(null);
  // P0f: hook is fully controlled — filters derived from props, no internal state.
  // Eliminates the prop-sync bug where switching date ranges silently dropped reps
  // whose latest fetched-window date fell outside the new filter.
  const filters: LeaderboardFilters = useMemo(() => initialFilters || {}, [initialFilters]);

  // AbortController for cleanup
  const abortControllerRef = useRef<AbortController | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // L3: debounce timer for realtime-triggered refetches in aggregate mode.
  const realtimeRefetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // /break Edge Case 1: setTimeout closes over fetchWithCleanup at tick-time.
  // Use a ref kept current on every render so the timeout always reads the latest fetcher.
  const fetchWithCleanupRef = useRef<() => void>(() => {});

  // Calculate date range for queries
  const dateRange = useMemo(() => {
    const end = filters.endDate || new Date();
    const start = filters.startDate || new Date(end);
    // Default to single day (today) if no range specified
    return {
      start: toOrgDateKey(start),
      end: toOrgDateKey(end),
    };
  }, [filters.startDate, filters.endDate]);

  // Fetch leaderboard data
  const fetchLeaderboard = useCallback(
    async (signal?: AbortSignal, forceRefresh = false) => {
      if (!orgId || !supabase) {
        setLeaderboard([]);
        setCompanyWideLeaderboard([]);
        return;
      }

      // Check cache before fetching (unless force refresh)
      const cacheKey = getLbCacheKey(
        orgId,
        filters.sector,
        filters.podId,
        dateRange.start,
        dateRange.end
      );

      // P0-LBTOTAL S3: KPI ±1 sums + active roster. Guard (mandatory): a falsy
      // date key or empty org array makes the RPC return silent all-zeros
      // (kdc.date >= NULL / = ANY('{}') match nothing, NO error) — skip the
      // call entirely rather than misread "no data" as "kpi 0". The RPC gets
      // EXACTLY the same date strings the orgQuery passes to .gte/.lte.
      const sidecarOrgIds = includeCompanyWide && allOrgIds.length > 0 ? allOrgIds : [orgId];
      const kpiPromise =
        dateRange.start && dateRange.end && sidecarOrgIds.length > 0
          ? supabase.rpc("get_kpi_delta_sums", {
              p_org_ids: sidecarOrgIds,
              p_start: dateRange.start,
              p_end: dateRange.end,
            })
          : Promise.resolve(null);
      // Active roster — always a direct read of the _readable view (RLS applies
      // via security_invoker). The inactive filter lives in the HOOK,
      // pre-setState, ONLY — never in the page.
      const rosterPromise = supabase
        .from("organization_members_readable")
        .select("user_id")
        .eq("status", "active")
        .in("organization_id", sidecarOrgIds);

      // Resolve both sidecars → returns the active-rep set (null = roster
      // unknown, skip filtering) and stores kpiDeltas/activeRepIds state.
      const resolveSidecars = async (): Promise<Set<string> | null> => {
        const [kpiRes, rosterRes] = await Promise.all([kpiPromise, rosterPromise]);
        if (kpiRes && kpiRes.error) {
          // Likely the get_kpi_delta_sums grant/deploy is missing. Do NOT fall
          // back to a raw kpi_daily_compliance read — the PGRST 1000-row cap
          // would silently truncate long ranges. Fix the grant instead.
          logger.error("get_kpi_delta_sums failed — kpi deltas unavailable:", kpiRes.error);
          setKpiDeltas(new Map());
        } else {
          const map = new Map<string, number>();
          for (const row of (kpiRes?.data ?? []) as {
            rep_id: string;
            kpi_delta: number;
          }[]) {
            map.set(row.rep_id, row.kpi_delta);
          }
          setKpiDeltas(map);
        }
        if (rosterRes.error) {
          // Roster unknown — show rows unfiltered rather than hiding everyone.
          logger.error("active-roster fetch failed — skipping inactive filter:", rosterRes.error);
          activeRepIdsRef.current = null;
          setActiveRepIds(new Set());
          return null;
        }
        const set = new Set<string>(
          ((rosterRes.data ?? []) as { user_id: string }[]).map((r) => r.user_id)
        );
        activeRepIdsRef.current = set;
        setActiveRepIds(set);
        return set;
      };

      if (!forceRefresh) {
        const cached = getLbCache<LeaderboardEntry[]>(cacheKey, orgId);
        if (cached) {
          // Cache stores RAW rows; the inactive filter re-applies with a fresh
          // roster on every path so a status flip propagates within one fetch.
          const activeSet = await resolveSidecars();
          if (signal?.aborted) return;
          setLeaderboard(activeSet ? cached.filter((e) => activeSet.has(e.rep_id)) : cached);
          setIsLoading(false);
          return;
        }
      }

      setIsLoading(true);
      setError(null);

      try {
        // O3 (plan-orphan-ondemand): refresh orphan_precalls for this window BEFORE
        // reading the view. Issued as its OWN rpc call on purpose — never nest this
        // VOLATILE write inside a STABLE read function, which is the volatility
        // contract Postgres and PostgREST both lean on.
        // Non-fatal by design: a freshness failure must never break the leaderboard.
        // ⚠ Be honest about the worst case — it is NOT "one cron cycle stale". If this
        // call breaks permanently (a revoked grant, a runtime error in the function),
        // the whole on-demand architecture silently reverts to nightly-only refresh
        // and the ONLY signal is this warn, which nothing monitors. Degradation is
        // indefinite and invisible, not bounded. If orphan counts ever look frozen,
        // check this log line first.
        // Scope: the caller's own org only. The company-wide query below spans other
        // orgs and stays cron-fresh — ensure_orphans_fresh is per-org and membership
        // gated, so fanning it across every visible org would be N guarded writes for
        // a read the viewer does not own.
        const { error: ensureError } = await supabase.rpc("ensure_orphans_fresh", {
          p_organization_id: orgId,
          p_from: dateRange.start,
          p_to: dateRange.end,
        });
        if (ensureError) {
          logger.warn(
            "ensure_orphans_fresh failed — serving possibly stale orphan counts:",
            ensureError
          );
        }

        // Build org leaderboard query
        let orgQuery = supabase
          .from("daily_leaderboard_readable")
          .select("*")
          .eq("organization_id", orgId)
          .gte("date", dateRange.start)
          .lte("date", dateRange.end)
          .order("date", { ascending: false })
          .limit(1000);

        // Apply sector filter
        if (filters.sector) {
          orgQuery = orgQuery.eq("sector", filters.sector);
        }

        // Apply pod filter
        if (filters.podId) {
          orgQuery = orgQuery.eq("pod_id", filters.podId);
        }

        // Build company-wide query if needed
        let companyQuery = null;
        if (includeCompanyWide && allOrgIds.length > 0) {
          companyQuery = supabase
            .from("daily_leaderboard_readable")
            .select("*")
            .in("organization_id", allOrgIds)
            .gte("date", dateRange.start)
            .lte("date", dateRange.end)
            .order("date", { ascending: false })
            .limit(3000);

          if (filters.sector) {
            companyQuery = companyQuery.eq("sector", filters.sector);
          }
        }

        // Execute queries in parallel (sidecars ride the same batch)
        const [orgResult, companyResult, activeSet] = await Promise.all([
          orgQuery,
          companyQuery || Promise.resolve(null),
          resolveSidecars(),
        ]);

        // Check if aborted
        if (signal?.aborted) {
          return;
        }

        if (orgResult.error) {
          throw orgResult.error;
        }

        // Process org leaderboard
        // P0f: return raw daily rows; page-side computeSectorEntries handles aggregation.
        // Pre-aggregating here stripped the per-day `date` column the page filters against,
        // silently dropping reps whose latest fetched date fell outside the new filter window.
        // P0-LBTOTAL: inactive reps vanish from every consumer (filtered here,
        // pre-setState; the cache keeps RAW rows so the filter always runs
        // against the freshest roster).
        const orgData = (orgResult.data || []) as LeaderboardEntry[];
        setLeaderboard(activeSet ? orgData.filter((e) => activeSet.has(e.rep_id)) : orgData);

        // Cache the result
        setLbCache(cacheKey, orgData, orgId);

        // Process company-wide leaderboard
        if (companyResult && typeof companyResult === "object" && "data" in companyResult) {
          if (companyResult.error) {
            logger.error("Company leaderboard fetch error:", companyResult.error);
          } else {
            const cwData = (companyResult.data || []) as LeaderboardEntry[];
            setCompanyWideLeaderboard(
              activeSet ? cwData.filter((e) => activeSet.has(e.rep_id)) : cwData
            );
          }
        }
      } catch (err) {
        if ((err as Error).name === "AbortError") {
          return;
        }
        const message = err instanceof Error ? err.message : "Failed to fetch leaderboard";
        logger.error("useLeaderboard fetch error:", message);
        setError(message);
      } finally {
        setIsLoading(false);
      }
    },
    [orgId, allOrgIds, includeCompanyWide, dateRange, filters]
  );

  // Fetch with cleanup
  const fetchWithCleanup = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    await fetchLeaderboard(abortControllerRef.current.signal);
  }, [fetchLeaderboard]);

  // Forward-update the ref every render so the realtime debounce timeout
  // always invokes the latest fetcher.
  fetchWithCleanupRef.current = fetchWithCleanup;

  // L3: realtime tick handler. Aggregate mode → debounced refetch (300ms). Daily
  // mode → in-place patch (correct: daily array keyed exactly by (rep_id, date)).
  const handleRealtimeUpdate = useCallback(
    (newRecord: Record<string, unknown>) => {
      const entry = newRecord as unknown as LeaderboardEntry;

      logger.debug("[useLeaderboard] realtime tick received", {
        rep_id: entry.rep_id,
        date: entry.date,
        viewMode: filters.viewMode,
      });

      // Always invalidate the localStorage cache for the current key so any
      // future cache-miss path sees fresh state. Cheap; correct in both modes.
      if (orgId) {
        const cacheKey = getLbCacheKey(
          orgId,
          filters.sector,
          filters.podId,
          dateRange.start,
          dateRange.end
        );
        localStorage.removeItem(LB_CACHE_PREFIX + cacheKey);
      }

      // Aggregate mode → debounced refetch (300ms). The ref-forwarded
      // fetchWithCleanup is always the latest one, dodging stale-closure bugs
      // when filters change inside the debounce window.
      if (filters.viewMode === "aggregate") {
        if (realtimeRefetchTimeoutRef.current) {
          clearTimeout(realtimeRefetchTimeoutRef.current);
        }
        realtimeRefetchTimeoutRef.current = setTimeout(() => {
          fetchWithCleanupRef.current();
        }, 300);
        return;
      }

      // P0-LBTOTAL: never re-introduce an inactive rep via the realtime patch
      // (e.g. a recompute zeroing their historical row would otherwise insert
      // it back into the board). Roster unknown (null) → let the row through.
      const knownActive = activeRepIdsRef.current;
      if (knownActive && !knownActive.has(entry.rep_id)) {
        return;
      }

      // Daily mode — in-place optimistic replace by (rep_id, date) is correct.
      setLeaderboard((prev) => {
        const idx = prev.findIndex((e) => e.rep_id === entry.rep_id && e.date === entry.date);
        if (idx >= 0) {
          const updated = [...prev];
          updated[idx] = entry;
          return updated;
        }
        // New entry - add to front
        return [entry, ...prev];
      });

      // Update company-wide if entry matches our org list
      if (allOrgIds.includes(entry.organization_id)) {
        setCompanyWideLeaderboard((prev) => {
          const idx = prev.findIndex((e) => e.rep_id === entry.rep_id && e.date === entry.date);
          if (idx >= 0) {
            const updated = [...prev];
            updated[idx] = entry;
            return updated;
          }
          return [entry, ...prev];
        });
      }
    },
    [allOrgIds, orgId, filters.sector, filters.podId, filters.viewMode, dateRange]
  );

  // Realtime subscription
  const { status: realtimeStatus, isConnected: isRealtimeConnected } = useRealtimeSubscription<
    Record<string, unknown>
  >({
    channelName: `leaderboard:${orgId}`,
    table: "daily_leaderboard",
    filter: orgId ? `organization_id=eq.${orgId}` : undefined,
    events: LB_REALTIME_EVENTS,
    onInsert: handleRealtimeUpdate,
    onUpdate: handleRealtimeUpdate,
    enabled: enableRealtime && !!orgId,
  });

  // Fallback to polling when Realtime is not connected
  useEffect(() => {
    if (!orgId || !supabase) return;

    // Clear existing interval
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    // Only poll if Realtime is not connected
    if (!isRealtimeConnected && pollingInterval > 0) {
      logger.debug("useLeaderboard: Realtime not connected, falling back to polling");

      pollingIntervalRef.current = setInterval(() => {
        // Only poll when tab is visible
        if (document.visibilityState === "visible") {
          fetchWithCleanup();
        }
      }, pollingInterval);
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [orgId, isRealtimeConnected, pollingInterval, fetchWithCleanup]);

  // Clear any pending realtime-refetch debounce on unmount.
  useEffect(() => {
    return () => {
      if (realtimeRefetchTimeoutRef.current) {
        clearTimeout(realtimeRefetchTimeoutRef.current);
      }
    };
  }, []);

  // Initial fetch
  useEffect(() => {
    if (orgId) {
      fetchWithCleanup();
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [orgId, filters, fetchWithCleanup]);

  // Public methods
  const refresh = useCallback(async () => {
    // Force refresh bypasses cache
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    await fetchLeaderboard(abortControllerRef.current.signal, true);
  }, [fetchLeaderboard]);

  // P0f: hook is now controlled — pass new filters via the `filters` prop instead.
  // Public API kept stable so existing callers don't break; this is a no-op + warning.
  const setFilters = useCallback((_newFilters: LeaderboardFilters) => {
    logger.warn(
      "[useLeaderboard] setFilters called but hook is now controlled — pass filters via the `filters` prop instead"
    );
  }, []);

  return {
    leaderboard,
    companyWideLeaderboard,
    kpiDeltas,
    activeRepIds,
    isLoading,
    error,
    realtimeStatus,
    isRealtimeConnected,
    refresh,
    setFilters,
  };
}
